import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountDetailSchema,
  type AccountDetailDto,
  type AccountDto,
  type AccountGroupDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, patchJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { PageHeader, Section, LoadingState } from '../../shared/ui/page';
import { Facts, RecordFailure } from '../finance/detail';
import { ACCOUNTS, pathText, useAccountGroups } from './ChartOfAccountsPage';

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

/**
 * One account (`/accounts/:id`): every attribute of the revision in force (FRD-FIN-002), its
 * hierarchy path (FRD-FIN-011), its revision history (FRD-FIN-017) and its audit trail. A user
 * holding `ledger.account:change` may amend it or deactivate / reactivate it, each with a reason;
 * every change is a new revision and history is never rewritten (FRD-FIN-006, FIN-008, FIN-009).
 * The account ledger opens the posted transactions behind its balance (FRD-FIN-053).
 */
export function AccountDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'ledger.account:change');
  const canReport = hasCapability(session.data, 'ledger.journal:view');
  const [dialog, setDialog] = useState<'amend' | 'status' | null>(null);
  const detail = useQuery({
    queryKey: ['account', id],
    queryFn: () => getData(`${ACCOUNTS}/${id}`, AccountDetailSchema),
  });
  const back = (
    <Button component={RouterLink} to="/accounts" startIcon={<ArrowBackIcon />}>
      Chart of accounts
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the account" />;
  if (detail.isError) {
    return <RecordFailure title="Account" noun="account" error={detail.error} back={back} />;
  }
  const { account } = detail.data;
  const requires = [
    ['Cost centre', account.requires.costCenter],
    ['Project', account.requires.project],
    ['Client', account.requires.client],
    ['Supplier', account.requires.supplier],
    ['Employee', account.requires.employee],
    ['Asset', account.requires.asset],
  ] as const;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${account.code} — ${account.name}`}
        description={pathText(account.path)}
        actions={
          <>
            {back}
            {canReport && (
              <Button
                component={RouterLink}
                to={`/reports/account-ledger?account=${account.id}`}
                variant="outlined"
              >
                Account ledger
              </Button>
            )}
            {canChange && (
              <>
                <Button
                  variant="outlined"
                  color={account.status === 'active' ? 'warning' : 'primary'}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('status');
                  }}
                >
                  {account.status === 'active' ? 'Deactivate' : 'Reactivate'}
                </Button>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('amend');
                  }}
                >
                  Amend
                </Button>
              </>
            )}
          </>
        }
      />
      <Section title="Account">
        <Facts
          label="Account details"
          facts={[
            ['Code', account.code],
            ['Name', account.name],
            ['Status', <StatusChip key="s" status={account.status} />],
            ['Effective from', account.effectiveDate],
            ...account.path.map(
              (segment) =>
                [`${segment.level} group`, `${segment.code} ${segment.name}`] as [string, string],
            ),
            ['Account type', account.accountType],
            ['Financial statement', account.financialStatement],
            ['Normal balance', account.normalBalance],
            ['Posting account', yesNo(account.isPosting)],
            ['Control (system-controlled) account', yesNo(account.isSystemControlled)],
            ['Reconciliation required', yesNo(account.reconciliationRequired)],
            ['VAT applicable', yesNo(account.vatApplicable)],
            ['VAT treatment', account.vatTreatment],
            ['Module', account.moduleTag],
            ['CAPEX / OPEX', account.capexOpex],
            ['Direct / Indirect', account.directIndirect],
            [
              'Dimensions required',
              requires
                .filter(([, on]) => on)
                .map(([name]) => name)
                .join(', ') || 'None',
            ],
            ['Description', detail.data.description ?? '—'],
            ['Remarks', detail.data.remarks ?? '—'],
          ]}
        />
      </Section>
      <Revisions revisions={detail.data.revisions} />
      <RecordHistory recordType="ledger.account" recordId={account.id} />
      {dialog === 'amend' && (
        <AmendAccountDialog
          key={String(account.version)}
          detail={detail.data}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'status' && (
        <AccountStatusDialog
          account={account}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}

/** Every revision, newest first: what applied, from when, and why (FRD-FIN-017). */
function Revisions({ revisions }: { revisions: AccountDetailDto['revisions'] }) {
  return (
    <Section title="Revision history">
      <Table size="small" aria-label="Revision history">
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Effective</TableCell>
            <TableCell>Posting</TableCell>
            <TableCell>Control</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {revisions.map((revision) => (
            <TableRow key={revision.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{revision.validFrom.slice(0, 16)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {revision.validTo === null ? 'In force' : revision.validTo.slice(0, 16)}
              </TableCell>
              <TableCell>{revision.name}</TableCell>
              <TableCell>
                <StatusChip status={revision.status} />
              </TableCell>
              <TableCell>{revision.effectiveDate}</TableCell>
              <TableCell>{yesNo(revision.isPosting)}</TableCell>
              <TableCell>{yesNo(revision.isSystemControlled)}</TableCell>
              <TableCell>{revision.changeReason ?? 'COA import'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
}

function useAmend(accountId: string, version: number, onDone: () => void) {
  const session = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      patchJson(
        `${ACCOUNTS}/${accountId}`,
        body,
        AccountDetailSchema,
        session.data?.csrfToken ?? '',
        version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onDone();
    },
  });
}

/**
 * Deactivate or reactivate with a reason. An inactive account refuses new postings only; what was
 * posted to it stays (FRD-FIN-006, FIN-009).
 */
function AccountStatusDialog({ account, onClose }: { account: AccountDto; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const next = account.status === 'active' ? 'inactive' : 'active';
  const amend = useAmend(account.id, account.version, onClose);
  return (
    <FormDialog
      open
      title={`${next === 'inactive' ? 'Deactivate' : 'Reactivate'} ${account.code}`}
      submitLabel={next === 'inactive' ? 'Deactivate' : 'Reactivate'}
      canSubmit={reason.trim() !== ''}
      submitting={amend.isPending}
      error={amend.isError ? amend.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        amend.mutate({ status: next, reason: reason.trim() });
      }}
    >
      <Typography variant="body2">
        {next === 'inactive'
          ? 'An inactive account accepts no new postings. Its history and balances are kept.'
          : 'The account accepts postings again from its effective date.'}
      </Typography>
      <TextField
        label="Reason"
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </FormDialog>
  );
}

interface AmendValues {
  name: string;
  accountGroupId: string;
  accountType: string;
  financialStatement: string;
  normalBalance: string;
  isPosting: boolean;
  isSystemControlled: boolean;
  reconciliationRequired: boolean;
  vatApplicable: boolean;
  vatTreatment: string;
  moduleTag: string;
  capexOpex: string;
  directIndirect: string;
  requiresCostCenter: boolean;
  requiresProject: boolean;
  requiresClient: boolean;
  requiresSupplier: boolean;
  requiresEmployee: boolean;
  requiresAsset: boolean;
  effectiveDate: string;
  description: string;
  remarks: string;
}

function valuesOf(detail: AccountDetailDto): AmendValues {
  const a = detail.account;
  return {
    name: a.name,
    accountGroupId: a.accountGroupId,
    accountType: a.accountType,
    financialStatement: a.financialStatement,
    normalBalance: a.normalBalance,
    isPosting: a.isPosting,
    isSystemControlled: a.isSystemControlled,
    reconciliationRequired: a.reconciliationRequired,
    vatApplicable: a.vatApplicable,
    vatTreatment: a.vatTreatment,
    moduleTag: a.moduleTag,
    capexOpex: a.capexOpex,
    directIndirect: a.directIndirect,
    requiresCostCenter: a.requires.costCenter,
    requiresProject: a.requires.project,
    requiresClient: a.requires.client,
    requiresSupplier: a.requires.supplier,
    requiresEmployee: a.requires.employee,
    requiresAsset: a.requires.asset,
    effectiveDate: a.effectiveDate,
    description: detail.description ?? '',
    remarks: detail.remarks ?? '',
  };
}

/**
 * Amend any attribute of the account except its code (FRD-FIN-008, FIN-010), with a reason. Only
 * the attributes actually changed are sent; the API writes a new revision and audits each change.
 */
function AmendAccountDialog({
  detail,
  onClose,
}: {
  detail: AccountDetailDto;
  onClose: () => void;
}) {
  const initial = valuesOf(detail);
  const [values, setValues] = useState<AmendValues>(initial);
  const [reason, setReason] = useState('');
  const groups = useAccountGroups();
  const l4 = (groups.data ?? []).filter((g: AccountGroupDto) => g.level === 'L4');
  const amend = useAmend(detail.account.id, detail.account.version, onClose);
  const set = <K extends keyof AmendValues>(key: K, value: AmendValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  };
  const changed = (Object.keys(values) as (keyof AmendValues)[]).filter(
    (key) => values[key] !== initial[key],
  );
  const body = (): Record<string, unknown> => {
    const out: Record<string, unknown> = { reason: reason.trim() };
    for (const key of changed) {
      const value = values[key];
      out[key] = (key === 'description' || key === 'remarks') && value === '' ? null : value;
    }
    return out;
  };
  const text = (label: string, key: keyof AmendValues, helper?: string): ReactNode => (
    <TextField
      size="small"
      label={label}
      value={values[key]}
      helperText={helper}
      onChange={(e) => {
        set(key, e.target.value as never);
      }}
    />
  );
  const flag = (label: string, key: keyof AmendValues): ReactNode => (
    <FormControlLabel
      key={key}
      control={
        <Switch
          checked={values[key] as boolean}
          onChange={(e) => {
            set(key, e.target.checked as never);
          }}
        />
      }
      label={label}
    />
  );
  return (
    <FormDialog
      open
      title={`Amend ${detail.account.code}`}
      submitLabel="Save amendment"
      canSubmit={reason.trim() !== '' && changed.length > 0 && values.name.trim() !== ''}
      submitting={amend.isPending}
      error={amend.isError ? amend.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        amend.mutate(body());
      }}
    >
      <Alert severity="info" variant="outlined">
        The Chart of Accounts is approved Finance data. Amend it only as Finance has decided; the
        change applies to new postings, and earlier postings keep the revision they used.
      </Alert>
      {text('Name', 'name')}
      <TextField
        select
        size="small"
        label="L4 group"
        value={values.accountGroupId}
        onChange={(e) => {
          set('accountGroupId', e.target.value);
        }}
      >
        {l4.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.code} {group.name}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {text('Account type', 'accountType')}
        <TextField
          select
          size="small"
          label="Financial statement"
          value={values.financialStatement}
          sx={{ minWidth: 180 }}
          onChange={(e) => {
            set('financialStatement', e.target.value);
          }}
        >
          <MenuItem value="Balance Sheet">Balance Sheet</MenuItem>
          <MenuItem value="P&L">P&amp;L</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Normal balance"
          value={values.normalBalance}
          sx={{ minWidth: 150 }}
          onChange={(e) => {
            set('normalBalance', e.target.value);
          }}
        >
          <MenuItem value="Debit">Debit</MenuItem>
          <MenuItem value="Credit">Credit</MenuItem>
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {text('VAT treatment', 'vatTreatment')}
        {text('Module tag', 'moduleTag')}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {text('CAPEX / OPEX', 'capexOpex')}
        {text('Direct / Indirect', 'directIndirect')}
        <TextField
          size="small"
          type="date"
          label="Effective date"
          value={values.effectiveDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('effectiveDate', e.target.value);
          }}
        />
      </Stack>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
        {flag('Posting account', 'isPosting')}
        {flag('Control account', 'isSystemControlled')}
        {flag('Reconciliation required', 'reconciliationRequired')}
        {flag('VAT applicable', 'vatApplicable')}
      </Stack>
      <Typography variant="subtitle2">Dimensions required</Typography>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
        {flag('Cost centre', 'requiresCostCenter')}
        {flag('Project', 'requiresProject')}
        {flag('Client', 'requiresClient')}
        {flag('Supplier', 'requiresSupplier')}
        {flag('Employee', 'requiresEmployee')}
        {flag('Asset', 'requiresAsset')}
      </Stack>
      <TextField
        size="small"
        label="Description"
        multiline
        value={values.description}
        onChange={(e) => {
          set('description', e.target.value);
        }}
      />
      <TextField
        size="small"
        label="Remarks"
        multiline
        value={values.remarks}
        onChange={(e) => {
          set('remarks', e.target.value);
        }}
      />
      <TextField
        label="Reason for the amendment"
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </FormDialog>
  );
}
